import { useContext, useStore } from '@nuxtjs/composition-api';
import { computed, Ref } from 'vue';
import { IProfileState } from '~/store/profile/types';
import {
  ISanityDocuments,
  SanityDocumentSchema,
} from '~/types/sanity-documents';
import { ISanityObjects, SanityObjectSchema } from '~/types/sanity-objects';

type DocWithSlug = ISanityDocuments[SanityDocumentSchema] & {
  slug?: string;
};

type DocWithChat = DocWithSlug & {
  chat?: ISanityObjects[SanityObjectSchema.CHAT_CONFIG];
};

export const useSanityDocumentChat = (
  doc: Ref<DocWithChat | undefined>,
  relatedDoc?: Ref<DocWithSlug | undefined>,
) => {
  const { $sanityLookup } = useContext();
  const store = useStore<{ profile: IProfileState }>();
  const isLoggedIn = computed(() => store.state.profile.user.loggedIn);
  const documentChatConfig = computed(() => doc.value?.chat);
  const globalChatConfig = computed(
    () => $sanityLookup.getSiteConfig()?.data?.chat,
  );

  const channel = computed(() =>
    doc.value?._id
      ? `${process.env.pubnubChatEnv || 'no-env'}.${
          doc.value._type
        }.${doc.value._id.replace('.', '_')}`
      : '',
  );

  const group = computed(() =>
    relatedDoc?.value?._id
      ? `${relatedDoc.value._type}.${
          relatedDoc.value.slug || relatedDoc.value._id
        }`
      : '',
  );

  const title = computed(() =>
    doc.value?._id
      ? `${doc.value._type[0].toUpperCase() + doc.value._type.slice(1)} page: ${
          doc.value?.slug || doc.value?._id
        }`
      : '',
  );

  const shouldShowChat = computed(() => {
    return (
      isLoggedIn.value &&
      globalChatConfig.value?.isEnabled &&
      documentChatConfig.value?.isEnabled &&
      !!channel.value
    );
  });

  const shouldEnableMessaging = computed(() => {
    return (
      isLoggedIn.value &&
      globalChatConfig.value?.isMessagingEnabled &&
      documentChatConfig.value?.isMessagingEnabled
    );
  });

  return {
    channel,
    group,
    shouldShowChat,
    shouldEnableMessaging,
    title,
  };
};
